import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiMessageCircle, FiSend, FiUser, FiCalendar } = FiIcons;

const Contact = ({ standalone = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: '',
    guests: '',
    experience: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      dates: '',
      guests: '',
      experience: '',
      message: ''
    });
    
    setIsSubmitting(false);
    alert('Thank you! We\'ll contact you within 2 hours to discuss your birding adventure.');
  };

  const contactMethods = [
    {
      icon: FiPhone,
      title: 'Birding Concierge Hotline',
      subtitle: '24/7 Expert Support',
      contact: '+94 71 771 4922',
      action: 'tel:+94717714922',
      description: 'Speak directly with our birding experts for immediate assistance and real-time sighting updates.'
    },
    {
      icon: FiMessageCircle,
      title: 'WhatsApp Direct',
      subtitle: 'Instant Messaging',
      contact: '+94 71 771 4922',
      action: 'https://wa.me/94717714922',
      description: 'Get instant responses to your questions and receive daily sighting reports via WhatsApp.'
    },
    {
      icon: FiMail,
      title: 'Email Reservations',
      subtitle: 'Detailed Inquiries',
      contact: 'info@sinharajabirders.com',
      action: 'mailto:info@sinharajabirders.com',
      description: 'Send detailed requests for custom itineraries, group bookings, and special arrangements.'
    }
  ];

  const experienceOptions = [
    'Dawn Chorus Expedition',
    'Night Safari Adventure',
    'Photography Masterclass',
    'Conservation Experience',
    'Custom Birding Tour',
    'Multi-day Package'
  ];

  const quickFacts = [
    {
      icon: FiMapPin,
      title: 'Location',
      detail: 'Weddagala, Sinharaja Forest Reserve Border'
    },
    {
      icon: FiClock,
      title: 'Response Time',
      detail: 'Within 2 hours, 24/7 availability'
    },
    {
      icon: FiUser,
      title: 'Languages',
      detail: 'English, Sinhala, Tamil, German, French'
    },
    {
      icon: FiCalendar,
      title: 'Best Season',
      detail: 'December - March (Dry Season)'
    }
  ];

  return (
    <section className={`${standalone ? 'pt-32' : 'py-20'} relative overflow-hidden`}>
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="liquid-blob"></div>
        <div className="liquid-blob"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 glass px-6 py-3 rounded-full mb-6">
            <SafeIcon icon={FiPhone} className="w-5 h-5 text-green-400" />
            <span className="text-white font-medium">Direct Contact</span>
          </div>
          
          <h2 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            Plan Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">
              Birding Adventure
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Connect directly with our birding concierge team. No third-party bookings, 
            no hidden fees - just expert guidance and personalized service.
          </p>
        </motion.div>

        {/* Contact Methods */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          {contactMethods.map((method, index) => (
            <motion.a
              key={index}
              href={method.action}
              target={method.action.startsWith('http') ? '_blank' : undefined}
              rel={method.action.startsWith('http') ? 'noopener noreferrer' : undefined}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass p-8 rounded-2xl text-center hover:glass-strong transition-all duration-300 group cursor-pointer"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <SafeIcon icon={method.icon} className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2">{method.title}</h3>
              <p className="text-green-300 text-sm font-medium mb-3">{method.subtitle}</p>
              <p className="text-lg font-semibold text-white mb-4">{method.contact}</p>
              <p className="text-gray-300 text-sm leading-relaxed">{method.description}</p>
            </motion.a>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="glass-strong rounded-2xl p-8"
          >
            <h3 className="font-display text-2xl font-bold text-white mb-6">
              Request Custom Itinerary
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass rounded-xl text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                    placeholder="Dr. Jane Smith"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 glass rounded-xl text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                    placeholder="jane@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Phone (WhatsApp)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 glass rounded-xl text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                    placeholder="+1 555 123 4567"
                  />
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Preferred Dates</label>
                  <input
                    type="text"
                    name="dates"
                    value={formData.dates}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 glass rounded-xl text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                    placeholder="Feb 15-20, 2024"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Number of Guests</label>
                  <select
                    name="guests"
                    value={formData.guests}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 glass rounded-xl text-white border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                  >
                    <option value="">Select guests</option>
                    <option value="1">1 Guest</option>
                    <option value="2">2 Guests</option>
                    <option value="3">3 Guests</option>
                    <option value="4">4 Guests</option>
                    <option value="5+">5+ Guests</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-white font-medium mb-2">Preferred Experience</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 glass rounded-xl text-white border border-white/20 focus:border-green-400 focus:outline-none transition-colors"
                  >
                    <option value="">Select experience</option>
                    {experienceOptions.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">
                  Special Requests or Target Species
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 glass rounded-xl text-white placeholder-gray-400 border border-white/20 focus:border-green-400 focus:outline-none transition-colors resize-none"
                  placeholder="I'm particularly interested in photographing the Serendib Scops Owl and would like to know about current activity patterns..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary px-6 py-4 text-lg font-semibold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-dots scale-50">
                      <div></div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                    <span>Sending Request...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                    <span>Send Inquiry</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Quick Facts & Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="space-y-8"
          >
            {/* Quick Facts */}
            <div className="glass-strong rounded-2xl p-8">
              <h3 className="font-display text-2xl font-bold text-white mb-6">
                Essential Information
              </h3>
              
              <div className="space-y-6">
                {quickFacts.map((fact, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-green-400 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={fact.icon} className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{fact.title}</h4>
                      <p className="text-gray-300 text-sm">{fact.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Location Map */}
            <div className="glass-strong rounded-2xl overflow-hidden">
              <div className="relative h-64">
                <img
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="Sinharaja Forest Reserve Location"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="font-semibold text-white mb-1">
                    Weddagala, Sinharaja Forest Reserve
                  </h4>
                  <p className="text-gray-300 text-sm">
                    15-minute walk to UNESCO World Heritage Site entrance
                  </p>
                </div>
                
                <div className="absolute top-4 right-4 glass px-3 py-1 rounded-full">
                  <span className="text-white text-sm font-medium">📍 Location</span>
                </div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">GPS Coordinates</p>
                    <p className="text-gray-300 text-sm">6.4000°N, 80.4000°E</p>
                  </div>
                  <button className="btn-primary px-4 py-2 text-sm">
                    Get Directions
                  </button>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="glass p-6 rounded-2xl border-l-4 border-yellow-400">
              <h4 className="font-semibold text-white mb-2 flex items-center">
                <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2 text-yellow-400" />
                24/7 Emergency Contact
              </h4>
              <p className="text-gray-300 text-sm mb-3">
                For urgent assistance during your stay or travel emergencies.
              </p>
              <a
                href="tel:+94717714922"
                className="text-yellow-400 font-semibold hover:text-yellow-300 transition-colors"
              >
                +94 71 771 4922
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;